import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ShieldCheck, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutDialog({ open, onClose, planId, planName, priceLabel }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", instagram: "" });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accepted) {
      setError("Você precisa aceitar os Termos de Contratação e a Política de Privacidade.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: planId,
          customer: {
            name: form.name,
            email: form.email,
            phone: form.phone,
            company: form.company,
            instagram: form.instagram,
          },
        }),
      });
      if (!res.ok) throw new Error("Falha ao gerar checkout");
      const data = await res.json();
      // Persist a light reference so the return page can greet the customer
      try {
        localStorage.setItem(
          "triad_last_order",
          JSON.stringify({ order_nsu: data.order_nsu, plan_name: data.plan_name, customer: form })
        );
      } catch (_) {}
      window.location.href = data.checkout_url;
    } catch (err) {
      setError("Não foi possível iniciar o pagamento. Tente novamente em instantes.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="checkout-dialog"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-ink-950/80 backdrop-blur-md"
            onClick={loading ? undefined : onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-gradient-to-b from-ink-900/95 to-ink-950/95 backdrop-blur-2xl shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] p-8 md:p-10 max-h-[92vh] overflow-y-auto"
          >
            <button
              onClick={onClose}
              disabled={loading}
              data-testid="checkout-close"
              className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-soft mb-2">Contratação</div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-white tracking-tight">
              {planName}
            </h3>
            <div className="mt-1 text-white/60 text-sm">
              <span className="text-green-400 font-semibold">{priceLabel}</span> · assinatura mensal
            </div>

            <form onSubmit={handleSubmit} className="mt-7 space-y-4">
              <Field label="Nome completo" required value={form.name} onChange={update("name")} testid="checkout-name" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="E-mail" type="email" required value={form.email} onChange={update("email")} testid="checkout-email" />
                <Field label="WhatsApp" required value={form.phone} onChange={update("phone")} placeholder="(11) 99999-9999" testid="checkout-phone" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nome da empresa" value={form.company} onChange={update("company")} testid="checkout-company" />
                <Field label="Instagram" value={form.instagram} onChange={update("instagram")} placeholder="@suaempresa" testid="checkout-instagram" />
              </div>

              <label className="flex items-start gap-3 pt-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  data-testid="checkout-terms"
                  className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 accent-green-500 shrink-0"
                />
                <span className="text-white/60 text-xs leading-relaxed">
                  Li e aceito os <a href="/termos" target="_blank" className="text-blue-soft hover:underline">Termos de Contratação</a>, a{" "}
                  <a href="/privacidade" target="_blank" className="text-blue-soft hover:underline">Política de Privacidade</a> e as condições de cobrança mensal.
                </span>
              </label>

              {error && (
                <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3" data-testid="checkout-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="checkout-submit"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold text-white bg-[#22C55E] hover:bg-[#16A34A] shadow-[0_10px_30px_-8px_rgba(34,197,94,0.55)] hover:shadow-[0_18px_40px_-6px_rgba(34,197,94,0.75)] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Gerando checkout…</>
                ) : (
                  <>Continuar para pagamento →</>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 text-white/40 text-[10px] uppercase tracking-[0.2em] pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                Pagamento seguro via InfinitePay · PIX ou cartão
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, type = "text", value, onChange, required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-white/50 text-[11px] uppercase tracking-[0.2em]">
        {label} {required && <span className="text-green-400">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-blue-400/60 focus:bg-white/[0.06] transition-colors"
      />
    </label>
  );
}
